import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeaturesContext = createContext({});

export const useFeatures = () => useContext(FeaturesContext);

export const FeaturesProvider = ({ children }) => {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchFeatures = useCallback(async () => {
    try {
      const token = localStorage.getItem('dme_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const res = await axios.get(`${API_URL}/api/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFeatures(res.data.features || {});
    } catch (error) {
      console.error('Error fetching features:', error);
      // Set default features if fetch fails
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch features on mount and when user changes (login/logout)
  useEffect(() => {
    if (user) {
      fetchFeatures();
    } else {
      setFeatures({});
      setLoading(false);
    }
  }, [user, fetchFeatures]);

  // Check if a specific feature is enabled
  const isFeatureEnabled = useCallback((featureId) => {
    // If features not loaded yet, default to true to avoid hiding things incorrectly
    if (loading) return true;
    
    // If feature is not explicitly set, default to true (enabled)
    // Features are only hidden when explicitly set to false
    if (features[featureId] === undefined) return true;
    
    // Return the explicit value
    return features[featureId] === true;
  }, [features, loading]);

  // Refresh features (call after toggling in FeaturesManager)
  const refreshFeatures = useCallback(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  return (
    <FeaturesContext.Provider value={{ 
      features, 
      loading, 
      isFeatureEnabled, 
      refreshFeatures 
    }}>
      {children}
    </FeaturesContext.Provider>
  );
};

export default FeaturesContext;
